import { withDependencies, named, optional } from '@wix/thunderbolt-ioc'
import type {
	BrowserWindow,
	IDomSelectors,
	IPropsStore,
	IPageDidMountHandler,
	IPageWillUnmountHandler,
	Experiments,
	IPageServicesRegistrar,
	ICompsLifeCycle,
} from '@wix/thunderbolt-symbols'
import {
	PageFeatureConfigSymbol,
	MasterPageFeatureConfigSymbol,
	BrowserWindowSymbol,
	DomSelectorsSymbol,
	Props,
	pageIdSym,
	ExperimentsSymbol,
	PageServicesManagerSymbol,
	CompsLifeCycleSym,
} from '@wix/thunderbolt-symbols'
import type { PageAnchorsPageConfig, PageAnchorsMasterPageConfig, PageAnchorsData } from './types'
import { name } from './symbols'
import { createAnchorObserver } from './pageAnchorsUtils'
import { AnchorsDefinition } from '@wix/viewer-service-anchors/definition'
import { isExperimentOpen } from '@wix/thunderbolt-experiments'

/**
 * This is your feature.
 * You can get your configuration written in site-assets and viewer-model injected into your feature
 */
const pageAnchorsFactory = (
	pageFeatureConfig: PageAnchorsPageConfig,
	masterPageConfig: PageAnchorsMasterPageConfig,
	window: BrowserWindow,
	propsStore: IPropsStore,
	pageId: string,
	experiments: Experiments,
	domSelectors: IDomSelectors,
	compsLifeCycle: ICompsLifeCycle,
	servicesManagerGetter?: IPageServicesRegistrar
): IPageDidMountHandler & IPageWillUnmountHandler => {
	const servicesManager = servicesManagerGetter?.get()
	const isServicesInfra =
		isExperimentOpen(experiments, 'specs.thunderbolt.servicesInfra') || pageFeatureConfig.isBuilderComponentModel
	const isServiceOpen = servicesManager && isServicesInfra && servicesManager.hasService(AnchorsDefinition)

	const pageAnchorsObservers = pageFeatureConfig.pageAnchorsObservers.concat(masterPageConfig.pageAnchorsObservers)
	const activeAnchorObservers = pageFeatureConfig.activeAnchorObservers.concat(masterPageConfig.activeAnchorObservers)
	const anchors = [...new Set([...pageFeatureConfig.anchors, ...masterPageConfig.anchors]).values()]
	const headerCompId = pageFeatureConfig.headerComponentId
	const anchorsData: PageAnchorsData = {
		pageAnchorsObservers,
		activeAnchorObservers,
		anchors,
		pageId,
		headerCompId,
		siteOffset: masterPageConfig.siteOffset,
		reCheckAnchors: false,
	}
	const observeAnchors =
		pageId !== 'masterPage'
			? createAnchorObserver(anchorsData, window, propsStore, domSelectors, compsLifeCycle)
			: () => () => undefined

	const observeAnchorsService =
		pageId !== 'masterPage' && isServiceOpen
			? servicesManager!.getService(AnchorsDefinition).observeAnchors(anchorsData)
			: () => () => undefined

	let observersCleanup: () => void | undefined

	return {
		pageDidMount(): void {
			if (isServiceOpen) {
				observersCleanup = observeAnchorsService()
			} else if (pageAnchorsObservers.length || activeAnchorObservers.length) {
				observersCleanup = observeAnchors()
			}
		},
		pageWillUnmount(): void {
			if (observersCleanup) {
				observersCleanup()
			}
		},
	}
}

export const PageAnchors = withDependencies(
	[
		named(PageFeatureConfigSymbol, name),
		named(MasterPageFeatureConfigSymbol, name),
		BrowserWindowSymbol,
		Props,
		pageIdSym,
		ExperimentsSymbol,
		DomSelectorsSymbol,
		CompsLifeCycleSym,
		optional(PageServicesManagerSymbol),
	],
	pageAnchorsFactory
)
